/**
 * Organization Context and Hook
 *
 * Provides organization state management across the application.
 * Handles current organization selection, member permissions, and org switching.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../components/auth';
import {
  listOrganizations,
  getOrganization,
  getOrCreatePersonalOrg,
} from '../services/organizationsApi';

// Storage key for persisting selected organization
const STORAGE_KEY = 'portfolistic_current_org';

function safeGetItem(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

function safeSetItem(key, value) {
  try { localStorage.setItem(key, value); } catch { /* private browsing */ }
}

// Organization context
const OrganizationContext = createContext(null);

/**
 * Organization Provider Component
 *
 * Wraps the application to provide organization context.
 */
export const OrganizationProvider = ({ children }) => {
  const { user } = useAuth();

  // State
  const [organizations, setOrganizations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Load user's organizations
   */
  const loadOrganizations = useCallback(async () => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrg(null);
      setCurrentRole(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const orgs = await listOrganizations(user);
      setOrganizations(orgs);

      // If no organizations, create personal org
      if (orgs.length === 0) {
        const personalOrg = await getOrCreatePersonalOrg(user);
        setOrganizations([{ ...personalOrg, role: 'owner' }]);
        selectOrganization(personalOrg.id, 'owner');
        return;
      }

      // Try to restore previous selection
      const savedOrgId = safeGetItem(STORAGE_KEY);
      if (savedOrgId) {
        const savedOrg = orgs.find(o => String(o.id) === savedOrgId);
        if (savedOrg) {
          setCurrentOrg(savedOrg);
          setCurrentRole(savedOrg.role);
          setLoading(false);
          return;
        }
      }

      // Default to first organization
      if (orgs.length > 0) {
        setCurrentOrg(orgs[0]);
        setCurrentRole(orgs[0].role);
        safeSetItem(STORAGE_KEY, orgs[0].id.toString());
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load organizations when user changes
  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  /**
   * Select an organization
   */
  const selectOrganization = useCallback(async (orgId, knownRole = null) => {
    if (!user) return;

    // Find in cached list first
    const cachedOrg = organizations.find(o => String(o.id) === String(orgId));
    if (cachedOrg) {
      setCurrentOrg(cachedOrg);
      setCurrentRole(cachedOrg.role);
      safeSetItem(STORAGE_KEY, orgId.toString());
      return;
    }

    // Fetch from API
    try {
      const org = await getOrganization(user, orgId);
      setCurrentOrg(org);
      setCurrentRole(org.current_user_role || knownRole);
      safeSetItem(STORAGE_KEY, orgId.toString());

      // Update cache
      setOrganizations(prev => {
        const exists = prev.find(o => String(o.id) === String(orgId));
        if (exists) {
          return prev.map(o => String(o.id) === String(orgId) ? { ...org, role: org.current_user_role } : o);
        }
        return [...prev, { ...org, role: org.current_user_role }];
      });
    } catch (err) {
      console.error('Failed to select organization:', err);
      setError(err.message);
    }
  }, [user, organizations]);

  /**
   * Refresh current organization data
   */
  const refreshCurrentOrg = useCallback(async () => {
    if (!user || !currentOrg) return;

    try {
      const org = await getOrganization(user, currentOrg.id);
      setCurrentOrg(org);
      setCurrentRole(org.current_user_role);
    } catch (err) {
      console.error('Failed to refresh organization:', err);
    }
  }, [user, currentOrg]);

  /**
   * Refresh organizations list
   */
  const refreshOrganizations = useCallback(async () => {
    await loadOrganizations();
  }, [loadOrganizations]);

  /**
   * Check if current user has admin role in current org
   */
  const isAdmin = useCallback(() => {
    return currentRole === 'owner' || currentRole === 'admin';
  }, [currentRole]);

  /**
   * Check if current user is owner of current org
   */
  const isOwner = useCallback(() => {
    return currentRole === 'owner';
  }, [currentRole]);

  /**
   * Check if user can manage members
   */
  const canManageMembers = useCallback((targetRole = null) => {
    if (!currentRole) return false;
    if (currentRole === 'owner') return true;
    if (currentRole === 'admin') {
      return targetRole === null || targetRole === 'member' || targetRole === 'viewer';
    }
    return false;
  }, [currentRole]);

  /**
   * Check if user can invite members
   */
  const canInvite = useCallback(() => {
    return currentRole === 'owner' || currentRole === 'admin';
  }, [currentRole]);

  /**
   * Check if user can edit organization settings
   */
  const canEditSettings = useCallback(() => {
    return currentRole === 'owner' || currentRole === 'admin';
  }, [currentRole]);

  /**
   * Check if user can delete organization
   */
  const canDelete = useCallback(() => {
    return currentRole === 'owner';
  }, [currentRole]);

  // Context value
  const value = {
    // State
    organizations,
    currentOrg,
    currentRole,
    loading,
    error,

    // Actions
    selectOrganization,
    refreshCurrentOrg,
    refreshOrganizations,

    // Permission helpers
    isAdmin,
    isOwner,
    canManageMembers,
    canInvite,
    canEditSettings,
    canDelete,

    // Computed
    hasOrganizations: organizations.length > 0,
    orgCount: organizations.length,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

/**
 * Hook to access organization context
 */
export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

/**
 * Higher-order component to require organization context
 */
export const withOrganization = (Component) => {
  return function WithOrganizationComponent(props) {
    const orgContext = useOrganization();
    return <Component {...props} organization={orgContext} />;
  };
};

export default useOrganization;
